import React, { useMemo } from 'react';
import { gql, ErrorLike } from '@apollo/client';
import { Constrain, Breakpoint } from '@moda/om';
import { compact } from '@moda/portal-stanchions';
import { Loading } from '../Loading';
import { DesignerListingPageFragment } from '../../generated/types';
import { throwQueryError } from '../../lib/apollo/throwQueryError';
import { usePLPParams } from '../../hooks/usePLPParams';
import { useRedirectLegacyPLPQueryParams } from '../ProductListing/useRedirectLegacyPLPQueryParams';
import { BackToTop } from '../BackToTop/BackToTop';
import {
  PAGE_FILTER_AVAILABILITY_FACET_FRAGMENT,
  PAGE_FILTER_NAME_SLUG_FACET_FRAGMENT,
  PAGE_FILTER_FRAGMENT
} from '../PageFilter';
import { DesignerListingResults } from './DesignerListingResults';
import { DesignerListingDesktop } from './DesignerListingDesktop';
import { DesignerListingMobile } from './DesignerListingMobile';

export const DESIGNER_LISTING_PAGE_FRAGMENT = gql`
  fragment DesignerListingPageFragment on CollectionResult {
    availability {
      ...PageFilterAvailabilityFacetFragment
    }
    designers {
      ...PageFilterNameSlugFacetFragment
    }
    ...PageFilterFragment
  }
  ${PAGE_FILTER_AVAILABILITY_FACET_FRAGMENT}
  ${PAGE_FILTER_NAME_SLUG_FACET_FRAGMENT}
  ${PAGE_FILTER_FRAGMENT}
`;

interface DesignerListingProps {
  headerText: string;
  queryResult?: DesignerListingPageFragment | null;
  error?: ErrorLike;
  loading: boolean;
}

export const DesignerListing: React.FC<DesignerListingProps> = ({
  headerText,
  queryResult,
  error,
  loading
}) => {
  const {
    params: { category, availability }
  } = usePLPParams();

  useRedirectLegacyPLPQueryParams();

  if (error) throwQueryError(error);

  const allDesigners = useMemo(() => compact(queryResult?.designers ?? []), [queryResult]);
  const designers = useMemo(
    () =>
      category != null || availability.length > 0
        ? allDesigners.filter(designer => designer.totalItems)
        : allDesigners,
    [allDesigners, category, availability]
  );

  if (!queryResult) return <Loading />;

  return (
    <>
      <Constrain className="DesignerListing">
        <Breakpoint gt="md">
          <DesignerListingDesktop headerText={headerText} queryResult={queryResult}>
            <DesignerListingResults designers={designers} loading={loading} />
          </DesignerListingDesktop>
        </Breakpoint>

        <Breakpoint lt="md">
          <DesignerListingMobile headerText={headerText} queryResult={queryResult}>
            <DesignerListingResults designers={designers} loading={loading} />
          </DesignerListingMobile>
        </Breakpoint>
      </Constrain>
      <BackToTop />
    </>
  );
};
